import React, { useMemo, useState } from 'react';
import clsx from 'clsx';

import { useStyles } from './style';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Switch,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Dropdown } from '../../../../shared/components/dropdown';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import StarIcon from '@material-ui/icons/Star';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import { ThemedButton } from '../../../../shared/components/themed-button';
import { OverlayModal } from '../../../../shared/components/overlay-modal';
import { Dialog } from '../../../../shared/components/dialog';
import { useUpdateFreePlanSupplierMutation } from '../../../../api/user';
import { PlanTypes } from '../CurrentPlan';
import { useScreenSize } from '../../../../shared/hooks/use-screen-size';
import { PipedriveScheduler } from '../PipedriveScheduler';
import { useGetStripeProductsQuery } from '../../../../api/stripe-product';
import { LocalStorageService } from '../../../../shared/services/localStorage.service';
import { formatCurrency } from '../../../../shared/helpers/format-currency';

interface Props {
  backStep: () => void;
  setBillYearly: (isYearly: boolean) => void;
  setNewPlan: (plan: PlanTypes) => void;
  isYearly?: boolean;
  currentPlan: PlanTypes;
  continueToPayment: () => void;
}

export const UpgradePlan: React.FC<Props> = ({ backStep, continueToPayment, setBillYearly, setNewPlan, currentPlan, isYearly = true }) => {
  const classes = useStyles();
  const { isMobile } = useScreenSize();
  const [isDropDownOpen, setIsDropDownOpen] = useState(false);
  const [freePlanAlert, setFreePlanAlert] = useState(false);
  const [isOpenScheduleModal, setIsOpenScheduleModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanTypes | null>(null);
  const [updateFreePlanSupplier] = useUpdateFreePlanSupplierMutation();
  const countryCode = LocalStorageService.getItem('country') || 'au';
  const { data: products } = useGetStripeProductsQuery({country_code: countryCode});
  
  const productPrice = (productKey: string, isYearly: boolean) => {
    const product = products?.stripe_products?.find(
      (p) => p.product_key === productKey
    );

    if(!product) {
      return 0;
    }

    const price = product.stripe_prices.find(
      (p) => p.interval === (isYearly ? 'year' : 'month')
    );

    if(!price) {
      return 0;
    }

    return price?.unit_amount;
  };

  const handleDropDownOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDropDownOpen(!isDropDownOpen);
  };

  const closeDropDown = () => {
    setIsDropDownOpen(false);
  };

  const onItemClick = (key: PlanTypes) => {
    setSelectedPlan(key);
    setNewPlan(key);
    closeDropDown();
  };

  const confirmFreePlan = () => {
    updateFreePlanSupplier().then((res) => {
      if ('data' in res && res.data?.success) { 
        window.location.reload();
      }
    })
  }

  return (
    <Box className={classes.root}>
      {
        isMobile
          ? <>
              <Box className={classes.backContainer} onClick={backStep}>
                <ArrowBackIcon className={classes.backChevron} /> {
                  currentPlan === 'free'
                    ? (selectedPlan === 'pro_lite' || selectedPlan === 'unlimited' || selectedPlan === 'enterprise')
                      ? 'Unlock Your Full Potential'
                      : 'Upgrade to Pro & Save Today!'
                    : 'Upgrade to Keep Orders Flowing'
                }
              </Box>
            </>
          : <>
              <Box className={classes.backContainer} onClick={backStep}>
                <ChevronLeftIcon className={classes.backChevron} /> Back
              </Box>
              <Typography className={classes.subHeader1}>
                {
                  currentPlan === 'free'
                    ? (selectedPlan === 'pro_lite' || selectedPlan === 'unlimited' || selectedPlan === 'enterprise')
                      ? 'Unlock Your Full Potential'
                      : 'Upgrade to Pro & Save Today!'
                    : 'Upgrade to Keep Orders Flowing'
                }
              </Typography>
            </>
      }
      <Typography className={classes.subHeader2}>
        {
          currentPlan === 'free'
            ? `You're currently on the Free plan. Upgrade now to unlock more features, automate your workflow, and maximise your ordering efficiency.`
            : 'Your trial has almost finished - select a plan to continue receiving orders and unlock advanced features'
        }
      </Typography>
      <Box className={classes.billedSwitch}>
        <span className={clsx(classes.billedMonthly, !isYearly && classes.billedActive)}>
          Billed Monthly
        </span>
        <Switch
            checked={isYearly}
            onChange={(e) => setBillYearly(e.currentTarget.checked)}
            color='primary'
            classes={{
              thumb: classes.thumb,
              track: classes.track,
              checked: classes.checked,
            }}
          />
        <span className={clsx(classes.billedAnnually, isYearly && classes.billedActive)}>
          Billed Annually <span className={classes.save20}>(SAVE 20%)</span>
        </span>
      </Box>

      <Grid container spacing={4} >
        <Grid item xs={12} md={12}>
          <Card className={clsx(classes.card, classes.billedContainer)}>
            <CardContent className={classes.cardContent}>
              <Box className={classes.bodyContainer}>
                <Typography variant="h6" className={classes.cardFeatureTitle}>
                  {
                    selectedPlan === null
                      ? 'How many orders do you process a month'
                      : 'Confirm Your Plan & Set Up Payment'
                  }
                </Typography>
                <Dropdown
                  dropdownClass={classes.dropdown}
                  align='left'
                  itemClass={classes.item}
                  items={[
                    <div className={classes.selectItem} key='free' onClick={() => onItemClick('free')}>
                      <span><strong>Free</strong> - Small Supplier (receive purchase orders only)</span>
                    </div>,
                    <div className={classes.selectItem} key='pro_lite' onClick={() => onItemClick('pro_lite')}>
                      <strong>Pro - Lite </strong> - (up to 100 orders) <br />
                      {
                        isYearly
                          ? <span className={classes.subText}>{formatCurrency(productPrice('pro_lite', isYearly)/1200, 0, products?.price_country_code, products?.price_country_currency)}/month or {formatCurrency(productPrice('pro_lite', isYearly)/100, 0, products?.price_country_code, products?.price_country_currency)}/year <span className={classes.save20}>(Save 20%)</span></span>
                          : <span className={classes.subText}>{formatCurrency(productPrice('pro_lite', isYearly)/100, 0, products?.price_country_code, products?.price_country_currency)}/month</span>
                      }
                    </div>,
                    <div className={clsx(classes.selectItem, classes.recommendedItem)} key='unlimited' onClick={() => onItemClick('unlimited')}>
                    <strong>Pro - Standard</strong> - (up to 500 orders) <br />
                    {
                      isYearly
                        ? <span className={classes.subText}>{formatCurrency(productPrice('unlimited', isYearly)/1200, 0, products?.price_country_code, products?.price_country_currency)}/month or {formatCurrency(productPrice('unlimited', isYearly)/100, 0, products?.price_country_code, products?.price_country_currency)}/year <span className={classes.save20}>(Save 20%)</span></span>
                        : <span className={classes.subText}>{formatCurrency(productPrice('unlimited', isYearly)/100, 0, products?.price_country_code, products?.price_country_currency)}/month</span>
                    }
                    <Chip
                      icon={<StarIcon className={classes.recommendedChipIcon} fontSize='small' />}
                      label="RECOMMENDED"
                      className={classes.recommendedChipTable}
                    />
                  </div>,
                    <div className={classes.selectItem} key='enterprise' onClick={() => onItemClick('enterprise')}>
                      Enterprise / Custom (500+ orders) <br />
                      <strong>Talk to Us</strong>
                    </div>,
                  ]}
                  isOpen={isDropDownOpen}
                  onClose={closeDropDown}
                  triggerBtn={
                    <div onClick={handleDropDownOpen} className={clsx([classes.inputField, isDropDownOpen && classes.active])}>
                      {selectedPlan === null
                        ? <span>- Choose a plan based on your average monthly orders -</span>
                        : (
                          <div className={classes.selectedItem}>
                            {
                              isMobile
                                ? <>
                                    {selectedPlan === 'free' && <span>You've selected:<strong> Free - Small Supplier</strong> (receive purchase orders only)</span>}
                                    {selectedPlan === 'pro_lite' && isYearly && <span>You've selected: <strong> Pro - Lite</strong> - (up to 100 orders) <strong>{formatCurrency(productPrice('pro_lite', isYearly)/1200, 0, products?.price_country_code, products?.price_country_currency)}/month or {formatCurrency(productPrice('pro_lite', isYearly)/100, 0, products?.price_country_code, products?.price_country_currency)}/year</strong></span>}
                                    {selectedPlan === 'pro_lite' && !isYearly && <span>You've selected: <strong> Pro - Lite</strong> - (up to 100 orders) <strong>{formatCurrency(productPrice('pro_lite', isYearly)/100, 0, products?.price_country_code, products?.price_country_currency)}/month</strong></span>}
                                    {selectedPlan === 'unlimited' && isYearly && <span>You've selected: <strong> Pro - Standard (Recommended)</strong> - (up to 500 orders) <strong>{formatCurrency(productPrice('unlimited', isYearly)/1200, 0, products?.price_country_code, products?.price_country_currency)}/month or {formatCurrency(productPrice('unlimited', isYearly)/100, 0, products?.price_country_code, products?.price_country_currency)}/year</strong></span>}
                                    {selectedPlan === 'unlimited' && !isYearly && <span>You've selected: <strong> Pro - Standard (Recommended)</strong> - (up to 500 orders) <strong>{formatCurrency(productPrice('unlimited', isYearly)/100, 0, products?.price_country_code, products?.price_country_currency)}/month</strong></span>}
                                    {selectedPlan === 'enterprise' && <span>You've selected: <strong>Enterprise / Custom (500+ orders)</strong> <strong>Talk to Us</strong></span>}
                                  </>
                                : <>
                                    <CheckIcon className={classes.checkIcon} />
                                    {selectedPlan === 'free' && <span>You've selected:<strong> Free - Small Supplier</strong> (receive purchase orders only)</span>}
                                    {selectedPlan === 'pro_lite' && isYearly && <span>You've selected: <strong> Pro - Lite</strong> - (up to 100 orders) <br /><strong>{formatCurrency(productPrice('pro_lite', isYearly)/1200, 0, products?.price_country_code, products?.price_country_currency)}/month or {formatCurrency(productPrice('pro_lite', isYearly)/100, 0, products?.price_country_code, products?.price_country_currency)}/year</strong></span>}
                                    {selectedPlan === 'pro_lite' && !isYearly && <span>You've selected: <strong> Pro - Lite</strong> - (up to 100 orders) <br /><strong>{formatCurrency(productPrice('pro_lite', isYearly)/100, 0, products?.price_country_code, products?.price_country_currency)}/month</strong></span>}
                                    {selectedPlan === 'unlimited' && isYearly && <span>You've selected: <strong> Pro - Standard (Recommended)</strong> - (up to 500 orders) <br /><strong>{formatCurrency(productPrice('unlimited', isYearly)/1200, 0, products?.price_country_code, products?.price_country_currency)}/month or {formatCurrency(productPrice('unlimited', isYearly)/100, 0, products?.price_country_code, products?.price_country_currency)}/year</strong></span>}
                                    {selectedPlan === 'unlimited' && !isYearly && <span>You've selected: <strong> Pro - Standard (Recommended)</strong> - (up to 500 orders) <br /><strong>{formatCurrency(productPrice('unlimited', isYearly)/100, 0, products?.price_country_code, products?.price_country_currency)}/month</strong></span>}
                                    {selectedPlan === 'enterprise' && <span>You've selected: <strong>Enterprise / Custom (500+ orders)</strong><br /><strong>Talk to Us</strong></span>}
                                  </>
                            }
                          </div>
                        )
                      }
                      {isDropDownOpen
                        ? <KeyboardArrowUpIcon className={clsx(classes.icon)} />
                        : <KeyboardArrowDownIcon className={clsx(classes.icon)} />
                      }
                    </div>
                  }
                />
                {['unlimited', 'pro_lite'].includes(selectedPlan || '') && <>
                  <Card className={clsx(classes.card, classes.unlimitedCard)}>
                    <CardContent className={classes.cardContent}>
                      <Box className={classes.unlimitedUpper}>
                        <Typography variant="h6" component="h3" className={classes.unlimitedCardFeatureTitle}>
                          Here’s what you’ll unlock on the {
                            selectedPlan === 'pro_lite'
                              ? <strong>Pro - Lite Plan</strong>
                              : <strong>Pro - Standard (Recommended) Plan</strong>
                          }
                        </Typography>
                      </Box>
                      <List dense className={classes.listItemWrapper}>
                        <ListItem disableGutters>
                          <ListItemIcon className={classes.listItemIcon}>
                            <CheckIcon className={classes.checkIcon} />
                          </ListItemIcon>
                          <ListItemText primary="Grow faster with automation of inventory, pricing, and accounting" className={classes.listItemText}/>
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemIcon className={classes.listItemIcon}>
                            <CheckIcon className={classes.checkIcon} />
                          </ListItemIcon>
                          <ListItemText primary="Reduce order processing costs by up to 95%" className={classes.listItemText}/>
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemIcon className={classes.listItemIcon}>
                            <CheckIcon className={classes.checkIcon} />
                          </ListItemIcon>
                          <ListItemText primary="Cut picking errors by 80% with smart digital fulfilment tools" className={classes.listItemText}/>
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemIcon className={classes.listItemIcon}>
                            <CheckIcon className={classes.checkIcon} />
                          </ListItemIcon>
                          <ListItemText primary="Gain real-time insights and analytics" className={classes.listItemText}/>
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemIcon className={classes.listItemIcon}>
                            <CheckIcon className={classes.checkIcon} />
                          </ListItemIcon>
                          <ListItemText primary="Integrate smoothly with your accounting systems like Xero, Quickbooks and SAP" className={classes.listItemText}/>
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemIcon className={classes.listItemIcon}>
                            <CheckIcon className={classes.checkIcon} />
                          </ListItemIcon>
                          <ListItemText primary="Ideal for growing suppliers looking to scale" className={classes.listItemText}/>
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                  <Box className={classes.paymentContainer}>
                  {
                      selectedPlan === currentPlan
                        ? <span className={classes.currentPlanText}>Your current plan is the {currentPlan === 'unlimited' ? 'Pro - Standard' : 'Pro - Lite'} Plan.</span>
                        : <>
                            <ThemedButton
                              onClick={continueToPayment}
                              title='Continue To Secure Payment'
                              buttonStyle='blueDarkButton'
                              width={'60%'}
                            />
                            <Typography variant="h6" component="h3" className={classes.secureText}>
                              🔒 Secure, encrypted checkout
                            </Typography>
                          </>
                    }
                  </Box>
                  </>
                }
                {selectedPlan === 'free' && <>
                  <Card className={clsx(classes.card, classes.unlimitedCard)}>
                    <CardContent className={classes.cardContent}>
                      <Box className={classes.freeUpper}>
                        <Typography variant="h6" component="h3" className={classes.unlimitedCardFeatureTitle}>
                          Here’s what you have on the <strong>Free Plan</strong>
                        </Typography>
                      </Box>
                      <List dense className={classes.freeListItemWrapper}>
                        <ListItem disableGutters>
                          <ListItemIcon className={classes.listItemIcon}>
                            <CheckIcon className={classes.checkIcon} />
                          </ListItemIcon>
                          <ListItemText primary="List your full product range to unlimited customers" className={classes.listItemText}/>
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemIcon className={classes.listItemIcon}>
                            <CheckIcon className={classes.checkIcon} />
                          </ListItemIcon>
                          <ListItemText primary="Receive and manage unlimited purchase orders effortlessly" className={classes.listItemText}/>
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemIcon className={classes.listItemIcon}>
                            <CloseIcon className={classes.closeIcon} />
                          </ListItemIcon>
                          <ListItemText primary="Integrate smoothly with your accounting systems like Xero, Quickbooks and SAP" className={classes.listItemText}/>
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemIcon className={classes.listItemIcon}>
                            <CloseIcon className={classes.closeIcon} />
                          </ListItemIcon>
                          <ListItemText primary="Reduce order processing costs by up to 95%" className={classes.listItemText}/>
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemIcon className={classes.listItemIcon}>
                            <CloseIcon className={classes.closeIcon} />
                          </ListItemIcon>
                          <ListItemText primary="Cut picking errors by 80% with smart digital fulfilment tools & inventory management" className={classes.listItemText}/>
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemIcon className={classes.listItemIcon}>
                            <CheckIcon className={classes.checkIcon} />
                          </ListItemIcon>
                          <ListItemText primary="Perfect for small suppliers getting started" className={classes.listItemText}/>
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                  <Box className={classes.paymentContainer}>
                    {
                      selectedPlan === currentPlan
                        ? <span className={classes.currentPlanText}>Your current plan is the Free Plan.</span>
                        : <ThemedButton
                            onClick={() => {
                              setFreePlanAlert(true);
                            }}
                            title='Change to Free Plan (Limited Features)'
                            buttonStyle='secondaryBordered'
                            width={'60%'}
                          />
                    }
                    </Box>
                </>}
                {selectedPlan === 'enterprise' && <>
                  <Card className={clsx(classes.card, classes.unlimitedCard)}>
                    <CardContent className={classes.cardContent}>
                      <Box className={classes.unlimitedUpper}>
                        <Typography variant="h6" component="h3" className={classes.unlimitedCardFeatureTitle}>
                          Here’s what you’ll unlock on the <strong>Enterprise Plan</strong>
                        </Typography>
                      </Box>
                      <List dense className={classes.listItemWrapper}>
                        <ListItem disableGutters>
                          <ListItemIcon className={classes.listItemIcon}>
                            <CheckIcon className={classes.checkIcon} />
                          </ListItemIcon>
                          <ListItemText primary="Fully customised ordering app and white-label online store" className={classes.listItemText}/>
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemIcon className={classes.listItemIcon}>
                            <CheckIcon className={classes.checkIcon} />
                          </ListItemIcon>
                          <ListItemText primary="ERP integrations tailored exactly to your business needs" className={classes.listItemText}/>
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemIcon className={classes.listItemIcon}>
                            <CheckIcon className={classes.checkIcon} />
                          </ListItemIcon>
                          <ListItemText primary="Automate manual orders from Email, SMS & WhatsApp using advanced AI (coming soon)" className={classes.listItemText}/>
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemIcon className={classes.listItemIcon}>
                            <CheckIcon className={classes.checkIcon} />
                          </ListItemIcon>
                          <ListItemText primary="Personalised onboarding, dedicated support & priority service" className={classes.listItemText}/>
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemIcon className={classes.listItemIcon}>
                            <CheckIcon className={classes.checkIcon} />
                          </ListItemIcon>
                          <ListItemText primary="Perfect for established suppliers with custom requirements" className={classes.listItemText}/>
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                  <Box className={classes.paymentContainer}>
                    {
                      selectedPlan === currentPlan
                        ? <span className={classes.currentPlanText}>Your current plan is the Enterprise Plan.</span>
                        : <ThemedButton
                            onClick={() => {
                              setIsOpenScheduleModal(true);
                            }}
                            title='Schedule a Call'
                            buttonStyle='blueDarkButton'
                            width={'60%'}
                          />
                    }
                  </Box>
                </>}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <OverlayModal closeOnClickOutside={true} showClose={isMobile ? false : true} boxClassName={classes.modalBox} isOpen={isOpenScheduleModal} onClose={() => setIsOpenScheduleModal(false)}>
        <PipedriveScheduler />
      </OverlayModal>
      {freePlanAlert && <Dialog
        title={`Are you sure you want to change your Plan? If you continue, you will lose your data.`}
        onConfirm={confirmFreePlan}
        onCancel={() => setFreePlanAlert(false)}
        customClass={classes.dialog}
        alertMode={true}
      />}
    </Box>
)};
